import { GutenbergBlock } from '../types';

export const serializeBlocksToWordPress = (blocks: GutenbergBlock[]): string => {
  if (!blocks || blocks.length === 0) {
    return '';
  }

  const serializedBlocks = blocks.map(block => {
    switch (block.name) {
      case 'core/paragraph':
        return `<!-- wp:paragraph -->
<p>${block.attributes.content || ''}</p>
<!-- /wp:paragraph -->`;

      case 'core/heading':
        const level = block.attributes.level || 2;
        return `<!-- wp:heading {"level":${level}} -->
<h${level}>${block.attributes.content || ''}</h${level}>
<!-- /wp:heading -->`;

      case 'core/image':
        const { url, alt, caption } = block.attributes;
        const captionHtml = caption ? `<figcaption>${caption}</figcaption>` : '';
        return `<!-- wp:image -->
<figure class="wp-block-image"><img src="${url || ''}" alt="${alt || ''}"/>${captionHtml}</figure>
<!-- /wp:image -->`;

      case 'core/list':
        const values = block.attributes.values || '';
        const listItems = values.split('\n').filter((item: string) => item.trim()).map((item: string) => `<li>${item.trim()}</li>`).join('\n');
        return `<!-- wp:list -->
<ul>${listItems}</ul>
<!-- /wp:list -->`;

      case 'core/quote':
        const value = block.attributes.value || '';
        return `<!-- wp:quote -->
<blockquote class="wp-block-quote"><p>${value}</p></blockquote>
<!-- /wp:quote -->`;

      default:
        // Generic block serialization
        const attributes = Object.keys(block.attributes).length > 0 
          ? ` ${JSON.stringify(block.attributes)}` 
          : '';
        return `<!-- wp:${block.name}${attributes} -->
<div class="wp-block-${block.name.replace('core/', '')}">${block.attributes.content || ''}</div>
<!-- /wp:${block.name} -->`;
    }
  });

  return serializedBlocks.join('\n\n');
};

export const parseWordPressContent = (content: string): GutenbergBlock[] => {
  if (!content) return [];

  // Check if content is WordPress block format
  if (content.includes('<!-- wp:')) {
    return parseWordPressBlocks(content);
  }
  
  // Otherwise, parse as plain HTML
  return parsePlainHTML(content);
};

const parseWordPressBlocks = (content: string): GutenbergBlock[] => {
  const blocks: GutenbergBlock[] = [];
  const blockRegex = /<!-- wp:([^\/\s]+)(?:\s+({[^}]+}))? -->\s*([\s\S]*?)\s*<!-- \/wp:\1 -->/g;
  
  let match;
  while ((match = blockRegex.exec(content)) !== null) {
    const [, blockName, attributesJson, blockContent] = match;
    
    try {
      const attributes = attributesJson ? JSON.parse(attributesJson) : {};
      
      // Extract content based on block type
      let content = '';
      switch (blockName) {
        case 'paragraph':
          const pMatch = blockContent.match(/<p[^>]*>(.*?)<\/p>/s);
          content = pMatch ? pMatch[1] : '';
          break;
        case 'heading':
          const hMatch = blockContent.match(/<h(\d)[^>]*>(.*?)<\/h\1>/s);
          if (hMatch) {
            attributes.level = parseInt(hMatch[1]);
            content = hMatch[2];
          }
          break;
        case 'image':
          const imgMatch = blockContent.match(/<img[^>]+src="([^"]+)"[^>]*alt="([^"]*)"[^>]*\/?>/);
          if (imgMatch) {
            attributes.url = imgMatch[1];
            attributes.alt = imgMatch[2];
          }
          const captionMatch = blockContent.match(/<figcaption[^>]*>(.*?)<\/figcaption>/s);
          if (captionMatch) {
            attributes.caption = captionMatch[1];
          }
          break;
        case 'list':
          const listItems = blockContent.match(/<li[^>]*>(.*?)<\/li>/gs);
          if (listItems) {
            attributes.values = listItems.map(item => 
              item.replace(/<li[^>]*>(.*?)<\/li>/s, '$1')
            ).join('\n');
          }
          break;
        case 'quote':
          const quoteMatch = blockContent.match(/<p[^>]*>(.*?)<\/p>/s);
          if (quoteMatch) {
            attributes.value = quoteMatch[1];
          }
          break;
        default:
          content = blockContent;
      }

      blocks.push({
        clientId: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: `core/${blockName}`,
        isValid: true,
        attributes: { content, ...attributes },
        innerBlocks: []
      });
    } catch (error) {
      console.warn(`Failed to parse block ${blockName}:`, error);
    }
  }

  return blocks;
};

const parsePlainHTML = (content: string): GutenbergBlock[] => {
  const blocks: GutenbergBlock[] = [];
  
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;
  
  // Process each child element
  Array.from(tempDiv.children).forEach((element, index) => {
    const block = parseHTMLElement(element as HTMLElement, index);
    if (block) {
      blocks.push(block);
    }
  });
  
  // If no blocks were created (e.g., only text), create a paragraph
  if (blocks.length === 0 && content.trim()) {
    blocks.push({
      clientId: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: 'core/paragraph',
      isValid: true,
      attributes: { content: content.trim() },
      innerBlocks: []
    });
  }
  
  return blocks;
};

const parseHTMLElement = (element: HTMLElement, index: number): GutenbergBlock | null => {
  const clientId = `block-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
  
  switch (element.tagName.toLowerCase()) {
    case 'p':
      return {
        clientId,
        name: 'core/paragraph',
        isValid: true,
        attributes: { content: element.textContent || '' },
        innerBlocks: []
      };
      
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      const level = parseInt(element.tagName.charAt(1));
      return {
        clientId,
        name: 'core/heading',
        isValid: true,
        attributes: { 
          content: element.textContent || '',
          level: level
        },
        innerBlocks: []
      };
      
    case 'ul':
    case 'ol':
      const listItems = Array.from(element.querySelectorAll('li')).map(li => li.textContent || '').join('\n');
      return {
        clientId,
        name: 'core/list',
        isValid: true,
        attributes: { values: listItems },
        innerBlocks: []
      };
      
    case 'blockquote':
      const quoteText = element.textContent || '';
      return {
        clientId,
        name: 'core/quote',
        isValid: true,
        attributes: { value: quoteText },
        innerBlocks: []
      };
      
    case 'img':
      const img = element as HTMLImageElement;
      return {
        clientId,
        name: 'core/image',
        isValid: true,
        attributes: {
          url: img.src || '',
          alt: img.alt || '',
          caption: ''
        },
        innerBlocks: []
      };
      
    case 'figure':
      const imgElement = element.querySelector('img') as HTMLImageElement;
      const captionElement = element.querySelector('figcaption');
      if (imgElement) {
        return {
          clientId,
          name: 'core/image',
          isValid: true,
          attributes: {
            url: imgElement.src || '',
            alt: imgElement.alt || '',
            caption: captionElement?.textContent || ''
          },
          innerBlocks: []
        };
      }
      break;
      
    case 'div':
      // Handle divs that might contain content
      if (element.textContent?.trim()) {
        return {
          clientId,
          name: 'core/paragraph',
          isValid: true,
          attributes: { content: element.textContent.trim() },
          innerBlocks: []
        };
      }
      break;
      
    default:
      // For any other element, treat as paragraph
      if (element.textContent?.trim()) {
        return {
          clientId,
          name: 'core/paragraph',
          isValid: true,
          attributes: { content: element.textContent.trim() },
          innerBlocks: []
        };
      }
  }
  
  return null;
};
